// 工事取得とデータ取得を分ける
import { EventDropArg } from '@fullcalendar/core';
import { ColSpec, ResourceApi } from '@fullcalendar/resource-common';
import { useSnackbar } from 'notistack';
import {
  ChangeEvent,
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
  useContext
} from 'react';
import { updateEvent } from '../../../../apicalls/attendanceManages';
import { getConstructionDetails } from '../../../../apicalls/construction';
import {
  getSiteProcessStatusName,
  log,
  validateGanttPeriod
} from '../../../../utils';
import { checkResData } from '../../../../utils/common';
import { SITE_PROCESS_STATUSES } from '../../../../const/Const';
import { TConstruction } from '../../../../types/common';
import { EventResizeDoneArg } from '@fullcalendar/interaction';
import { UseFormClearErrors, UseFormGetValues } from 'react-hook-form';
import {
  GANTT_CHART_HTML_ID,
  GANTT_MINIMUM_HEIGHT,
  GANTT_RESOURCE_ALL_WIDTH,
  GANTT_RESOURCE_ALL_WIDTH_RESPONSIVE_RATIO,
  GANTT_RESOURCE_MIN_WIDTH_RATIO,
  GANTT_RESOURCE_TITLE_AND_ADD_BUTTON_WIDTH,
  GANTT_SEARCH_HTML_ID,
  GANTT_WINDOW_WIDTH_SMARTPHONE,
  validationMessage
} from '../../../../const/ProcessConst';
import { Typography } from '@material-ui/core';
import { ceil } from 'lodash';
import { useStyles } from './useStyles';
import { BasicSelect } from '../../input/BasicSelect';
import { AuthInfoContext } from '../../../../AuthContextProvider';
import { ROLE } from '../../../../const/Const';
import { useRecoilState } from 'recoil';
import {
  attendanceAssignTotallOfDate,
  attendanceGanttData,
  btnColor
} from '../../../../GlobalState';
import { createGanttResourceAndEvent } from '../../../../utils/attendancePage';

export type TProps = {
  constructId: number;
  isOpenAddModal: boolean;
  setIsOpenAddModal: Dispatch<boolean>;
  setIsLoading: Dispatch<boolean>;
  setBulkIsOpen: any;
  setBulkSelectId: any;
  initialVisibleRange: any;
};

export const useHooks = (props: TProps) => {
  const {
    constructId,
    isOpenAddModal,
    setIsOpenAddModal,
    setIsLoading,
    setBulkIsOpen,
    setBulkSelectId,
    initialVisibleRange
  } = props;

  const classes = useStyles();

  const [authInfo, setAuthInfo] = useContext(AuthInfoContext);

  // fullcalendar参照用
  const calendarRef = useRef<any>();

  // snackbar設定
  const { enqueueSnackbar } = useSnackbar();

  // 工事情報
  const [currentConstruction, setCurrentConstruction] =
    useState<TConstruction>();

  const [, setInitialGanttData] = useRecoilState(attendanceGanttData);
  const [, setAssignTotallOfDate] = useRecoilState(
    attendanceAssignTotallOfDate
  );

  // ローディング用（各モーダルClose時）
  const [isLoadingForModal, setIsLoadingForModal] = useState(false);

  // 工程編集モーダル
  const [isOpenEditModal, setIsOpenEditModal] = useState(false);
  const [editId, setEditId] = useState(0);
  // GanttViewに渡す関数でGanttViewの再レンダリンがが不要なものはmemo化
  const handleClickSiteProcessName = useCallback((id: number) => {
    setEditId(id);
    setIsOpenEditModal(true);
  }, []);

  // リソースエリアに表示する項目毎の設定
  const resourceAreaTitle: ColSpec = {
    field: 'title',
    headerContent: '社員名/協力会社名',
    headerClassNames: 'resource-default resource-width-large',
    cellClassNames: 'resource-default resource-width-large',
    cellContent: args => {
      return (
        <>
          <button
            className='resource-content-button'
            onClick={() => {
              if (args.resource) {
                handleClickSiteProcessName(
                  args.resource.extendedProps.attendance.id
                );
              }
            }}
          >
            <Typography>{args.resource?.title}</Typography>
          </button>
        </>
      );
    }
  };

  const resourceAreaStartDate: ColSpec = {
    field: 'startDate',
    headerContent: '開始日',
    headerClassNames: 'resource-default resource-width-medium',
    cellClassNames: 'resource-default resource-width-medium',
    cellContent: args => {
      return (
        <Typography>
          {args.resource?.extendedProps.attendance.start_date}
        </Typography>
      );
    }
  };
  const resourceAreaEndDate: ColSpec = {
    field: 'endDate',
    headerContent: '終了日',
    headerClassNames: 'resource-default resource-width-medium',
    cellClassNames: 'resource-default resource-width-medium',
    cellContent: args => {
      return (
        <Typography>
          {args.resource?.extendedProps.attendance.end_date || ''}
        </Typography>
      );
    }
  };
  const resourceAreaStatus: ColSpec = {
    field: 'status',
    headerContent: 'ステータス',
    headerClassNames: 'resource-default resource-width-medium',
    cellClassNames: 'resource-default resource-width-medium',
    cellContent: args => {
      const attendance = args.resource?.extendedProps.attendance;
      return (
        <BasicSelect
          className={`${classes.statusSelectBox} ${classes.statusSelectBoxInRow}`}
          value={attendance.status}
          disableUnderline
          disabled={authInfo?.role == ROLE['OUTSIDE_PARTNER']}
        >
          {SITE_PROCESS_STATUSES.map(value => (
            <option value={value} key={value}>
              {getSiteProcessStatusName(value)}
            </option>
          ))}
        </BasicSelect>
      );
    }
  };
  const resourceAreaSiteTaskCount: ColSpec = {
    field: 'totalling',
    headerContent: '集計人工',
    headerClassNames: 'resource-default resource-width-medium',
    cellClassNames: 'resource-default resource-width-medium',
    cellContent: (args: any) => {
      // 大工程が閉じてる場合は大工程＋小工程のタスク件数を表示
      return (
        <>
          <button
            className='resource-content-button'
            onClick={() => {
              if (args.resource) {
                setBulkSelectId(args.resource.extendedProps.attendance.id);
              }
              setBulkIsOpen(true);
            }}
          >
            <Typography>{`${args.fieldValue}`}</Typography>
          </button>
        </>
      );
    }
  };

  // リソースに表示する内容の設定
  const [initialResourceAreaColumns] = useState([
    resourceAreaTitle,
    resourceAreaSiteTaskCount
  ]);

  // リソースエリアの幅を求める。詳細表示時はリソースエリアが全て見えるようにリソースエリアを多くできるように計算。
  const calculateResourceAreaWidth = (currentDisplayAll = false) => {
    const innerWidth = window.innerWidth;
    let resourceAreaWidth = `${GANTT_RESOURCE_TITLE_AND_ADD_BUTTON_WIDTH}px`;
    const element = document.getElementById(GANTT_CHART_HTML_ID);
    let width = 0;
    if (element) {
      const clientRect = element.getBoundingClientRect();
      width = clientRect.width;
    }
    if (currentDisplayAll) {
      if (width > GANTT_RESOURCE_ALL_WIDTH) {
        resourceAreaWidth = `${GANTT_RESOURCE_ALL_WIDTH}px`;
      } else {
        resourceAreaWidth = `${ceil(
          width * GANTT_RESOURCE_ALL_WIDTH_RESPONSIVE_RATIO
        )}px`;
      }
    } else {
      if (
        width * GANTT_RESOURCE_MIN_WIDTH_RATIO >
        GANTT_RESOURCE_TITLE_AND_ADD_BUTTON_WIDTH
      ) {
        resourceAreaWidth = `${GANTT_RESOURCE_TITLE_AND_ADD_BUTTON_WIDTH}px`;
      } else {
        resourceAreaWidth = `${GANTT_RESOURCE_MIN_WIDTH_RATIO * 100}%`;
      }
    }

    // スマホ向け
    if (innerWidth < GANTT_WINDOW_WIDTH_SMARTPHONE) {
      resourceAreaWidth = `${GANTT_RESOURCE_MIN_WIDTH_RATIO * 100}%`;
    }

    return resourceAreaWidth;
  };

  // ガントチャート上での工程編集
  // GanttViewに渡す関数でGanttViewの再レンダリンがが不要なものはmemo化
  const handleEdit = useCallback(
    (
      eventInfo: EventDropArg | EventResizeDoneArg,
      inputConstructId: number
    ) => {
      setIsLoading(true);
      const resourceId = eventInfo.event._def.resourceIds
        ? Number(eventInfo.event._def.resourceIds[0])
        : undefined;
      const oldResourceId = eventInfo.oldEvent._def.resourceIds
        ? Number(eventInfo.oldEvent._def.resourceIds[0])
        : undefined;

      if (
        eventInfo.event.startStr === eventInfo.oldEvent.startStr &&
        eventInfo.event.endStr === eventInfo.oldEvent.endStr &&
        resourceId == oldResourceId
      ) {
        return;
      }
      const date = new Date(eventInfo.event.startStr);
      const year = date.getFullYear();
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const day = date.getDate().toString().padStart(2, '0');
      const newStartDate = `${year}-${month}-${day}`;
      const artificial = Number(eventInfo.event.title);

      updateEvent(
        Number(eventInfo.event.id),
        newStartDate,
        artificial,
        resourceId
      ).then(resData => {
        checkResData(resData, enqueueSnackbar)
          .then(async (data: any) => {
            if (data.data.status) {
              const { resources, events, attendanceManageEventList } =
                await createGanttResourceAndEvent(
                  inputConstructId,
                  enqueueSnackbar,
                  initialVisibleRange.start,
                  initialVisibleRange.end
                );
              setAssignTotallOfDate(attendanceManageEventList);
              setInitialGanttData({
                resources,
                events
              });
              return;
            }
          })
          .finally(() => setIsLoading(false));
      });
    },
    [initialVisibleRange.start, initialVisibleRange.end]
  );

  // 工事ID切り替わり時の工程データ取得と初回の表示範囲の設定
  const handleChangeConstructId = async (
    startDate: string,
    endDate: string
  ) => {
    if (constructId > 0) {
      // 検索範囲を設定
      setIsLoading(true);
      const { resources, events, attendanceManageEventList } =
        await createGanttResourceAndEvent(
          constructId,
          enqueueSnackbar,
          startDate,
          endDate
        );
      setAssignTotallOfDate(attendanceManageEventList);
      setInitialGanttData({
        resources,
        events
      });

      setIsLoading(false);
    }
  };

  const [initialResourceAreaWidth, setInitialResourceAreaWidth] =
    useState('25%');

  // ウインドウサイズに合わせたガントのサイズ調整
  const displayAllChangeView = (currentDisplayAll = false) => {
    const calculateHeight = () => {
      // 画面内に検索エリア＋ガントエリア＋フッターが収まるようにガントの高さを計算する
      const windowHeight = document.documentElement.clientHeight;
      const headerElement = document.getElementsByTagName('header');
      const headerHeight =
        headerElement && headerElement[0]
          ? headerElement[0].getBoundingClientRect().height
          : 0;
      const footerElement = document.getElementsByClassName('footer');
      const footerHeight =
        footerElement && footerElement[0]
          ? footerElement[0].getBoundingClientRect().height
          : 0;
      const searchContainerElement =
        document.getElementById(GANTT_SEARCH_HTML_ID);
      const searchContainerHeight = searchContainerElement
        ? searchContainerElement.getBoundingClientRect().height
        : 0;
      let height =
        windowHeight - footerHeight - headerHeight - searchContainerHeight - 60; // 60はマージンなどの余白分
      if (height < GANTT_MINIMUM_HEIGHT) {
        height = GANTT_MINIMUM_HEIGHT;
      }

      return height;
    };

    const handleResizeWindow = () => {
      const height = calculateHeight();
      const resourceAreaWidth = calculateResourceAreaWidth(currentDisplayAll);
      if (calendarRef.current) {
        const calendarApi = calendarRef.current.getApi();
        calendarApi.setOption('height', height);
        calendarApi.setOption('resourceAreaWidth', resourceAreaWidth);
      }
    };
    window.addEventListener('resize', handleResizeWindow);

    // 詳細表示切り替え時の処理
    if (calendarRef.current) {
      const calendarApi = calendarRef.current.getApi();
      if (currentDisplayAll) {
        calendarApi.setOption('resourceAreaColumns', [
          resourceAreaTitle,
          resourceAreaStartDate,
          resourceAreaEndDate,
          resourceAreaStatus,
          resourceAreaSiteTaskCount
        ]);
      } else {
        calendarApi.setOption('resourceAreaColumns', [resourceAreaTitle]);
      }
      calendarApi.setOption(
        'resourceAreaWidth',
        calculateResourceAreaWidth(currentDisplayAll)
      );
    }

    // handleResizeWindowが複数イベントに登録されないように一度削除
    return () => {
      window.removeEventListener('resize', handleResizeWindow);
    };
  };

  useEffect(() => {
    // コンポーネント描画時にログ出力
    log.info('to process view mode: ganttchart');
    setIsLoading(true);
    displayAllChangeView();
  }, []);

  // 工事ID切り替わり時の工事データ取得
  useEffect(() => {
    // 工事が選択された時
    if (constructId > 0) {
      getConstructionDetails(constructId).then(resData => {
        checkResData(resData, enqueueSnackbar).then(data => {
          if (data) {
            const constructionDetail = data.data as TConstruction;
            setCurrentConstruction(constructionDetail);
          }
        });
      });
      // 工事ID切り替わり時の工程データ取得と初回の表示範囲の設定
      handleChangeConstructId(
        initialVisibleRange.start,
        initialVisibleRange.end
      );
    }
  }, [constructId, initialVisibleRange.start, initialVisibleRange.end]);

  return {
    calendarRef,
    isOpenAddModal,
    setIsOpenAddModal,
    isOpenEditModal,
    setIsOpenEditModal,
    editId,
    handleEdit,
    initialResourceAreaColumns,
    initialResourceAreaWidth,
    isLoadingForModal
  };
};
